import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../utils/auth';

// Backoff schedule for the extra visibility attempts in refresh(expectedEntityId).
// Memories are written to Azure AI Search via uploadDocuments, which acknowledges
// ACCEPTANCE — searchability follows asynchronously (typically sub-second). A
// refetch fired right after the POST can therefore miss the row it just wrote;
// these retries bridge that indexing gap without fabricating client state.
const REFRESH_RETRY_DELAYS_MS = [400, 800, 1600];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Group flat memories into one entry per family member (entity).
const groupByEntity = (memories) => {
  const memberGroups = {};

  memories.forEach(memory => {
    const entityName = memory.entity || 'Ukjent';
    if (!memberGroups[entityName]) {
      memberGroups[entityName] = {
        name: entityName,
        entityId: memory.entityId,
        entityType: memory.entityType,
        memories: [],
        lastUpdated: memory.timestamp
      };
    }
    memberGroups[entityName].memories.push(memory);

    // Keep track of most recent update
    if (new Date(memory.timestamp) > new Date(memberGroups[entityName].lastUpdated)) {
      memberGroups[entityName].lastUpdated = memory.timestamp;
    }
  });

  // Convert to array and sort by last updated
  return Object.values(memberGroups).sort(
    (a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated)
  );
};

/**
 * useFamilyMembers
 *
 * Shared source of truth for the user's family members. Fetches family_members
 * memories and groups them by entity (one entry per family member). Used by both
 * the Account → Family page (FamilyOverview) and the dashboard FamilyCard so the
 * fetch + group-by-entity logic lives in exactly one place.
 *
 * Returns:
 * - familyMembers: array of { name, entityId, entityType, memories, lastUpdated }
 *   sorted by most-recently-updated first. `memories.length` is the "X minner" count.
 * - loading: true until the FIRST fetch settles. Deliberately NOT flipped on
 *   refresh — doing so swapped the whole page for a skeleton and unmounted the
 *   add-member modal mid-success. The current list stays rendered while a
 *   refresh is in flight.
 * - refreshing: true while refresh() is in flight, for consumers that want a
 *   lightweight indicator without a skeleton swap.
 * - loadError: true when the first fetch failed and no fetch has succeeded
 *   since. Consumers show an error state with a retry (refresh()), never the
 *   empty list as if the user had no family. A failed refresh after a
 *   successful load keeps the list it already has.
 * - refresh(expectedEntityId?): re-fetch. When expectedEntityId is provided
 *   (the add-member flow passes the entityId it just wrote), the response is
 *   checked for that entity and re-fetched with backoff until it appears —
 *   see REFRESH_RETRY_DELAYS_MS. On exhaustion the server's list is rendered
 *   as-is (never a fabricated card) and a [family-visibility] warning is
 *   logged so exhaustion frequency is queryable.
 */
export function useFamilyMembers() {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Lets an in-flight retry loop stop cleanly if the consumer unmounts
  // mid-backoff (e.g. the user navigates away while the index catches up).
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // One fetch: pushes whatever the server returned into state and resolves with
  // the flat memory list so refresh() can check entity visibility. Errors are
  // caught here (state keeps its previous value) and surface as a null return.
  const fetchOnce = useCallback(async () => {
    try {
      const response = await authFetch('/memories?category=family_members');
      if (!response.ok) return null;
      const data = await response.json();
      if (!unmountedRef.current) {
        setFamilyMembers(groupByEntity(data.memories));
        setLoadError(false);
      }
      return data.memories;
    } catch (error) {
      console.error('Error fetching family members:', error);
      return null;
    }
  }, []);

  // Initial load — the ONLY path that drives `loading` (the consumers' full
  // skeletons render exactly once, before the first response).
  useEffect(() => {
    (async () => {
      const memories = await fetchOnce();
      if (!unmountedRef.current) {
        if (memories === null) setLoadError(true);
        setLoading(false);
      }
    })();
  }, [fetchOnce]);

  const refresh = useCallback(async (expectedEntityId) => {
    setRefreshing(true);
    try {
      const hasEntity = (memories) =>
        Array.isArray(memories) && memories.some(m => m.entityId === expectedEntityId);

      let memories = await fetchOnce();

      if (expectedEntityId && !hasEntity(memories)) {
        for (const backoffMs of REFRESH_RETRY_DELAYS_MS) {
          await delay(backoffMs);
          if (unmountedRef.current) return;
          memories = await fetchOnce();
          if (hasEntity(memories)) break;
        }
        if (!hasEntity(memories)) {
          console.warn(
            `[family-visibility] entityId=${expectedEntityId} absent from ` +
            `/memories after ${REFRESH_RETRY_DELAYS_MS.length + 1} attempts ` +
            `(${REFRESH_RETRY_DELAYS_MS.join('/')}ms backoff) — ` +
            'rendering the list the server returned'
          );
        }
      }
    } finally {
      if (!unmountedRef.current) setRefreshing(false);
    }
  }, [fetchOnce]);

  return { familyMembers, loading, loadError, refreshing, refresh };
}

export default useFamilyMembers;
